/**
 * Sparkline — tiny SVG line+area chart for a series of {label, value}.
 *
 * Drawn at the caller's container width × specified height. Hover tooltip
 * shows label + formatted value. Pure SVG, no charting library.
 *
 * Usage:
 *   <Sparkline
 *     data={[{label:'2026-01', value:500000}, {label:'2026-02', value:470000}]}
 *     height={56}
 *     accent="#8B3FC5"
 *     formatValue={(v) => `$${v.toLocaleString()}`}
 *   />
 */
import React, { useMemo, useState } from 'react';

export default function Sparkline({
  data = [],
  height = 56,
  accent = '#8B3FC5',
  formatValue = (v) => v?.toLocaleString?.() ?? String(v),
  showAxis = false,
  testId = 'sparkline',
}) {
  const [hoverIdx, setHoverIdx] = useState(null);

  const { points, minV, maxV, width } = useMemo(() => {
    const w = 260;  // viewBox width — scales with parent via preserveAspectRatio
    if (!data || data.length === 0) {
      return { points: [], minV: 0, maxV: 0, width: w };
    }
    const values = data.map((d) => Number(d.value) || 0);
    const minV = Math.min(...values);
    const maxV = Math.max(...values);
    const span = maxV - minV || 1;
    const padX = 6;
    const innerW = w - padX * 2;
    const padY = 6;
    const innerH = height - padY * 2;
    const stepX = data.length > 1 ? innerW / (data.length - 1) : 0;
    const points = data.map((d, i) => {
      const x = padX + i * stepX;
      const y = padY + innerH - ((Number(d.value) || 0) - minV) / span * innerH;
      return { x, y, value: Number(d.value) || 0, label: d.label };
    });
    return { points, minV, maxV, width: w };
  }, [data, height]);

  if (!data || data.length === 0) {
    return (
      <div className="text-[10px] text-slate-400 italic h-[56px] flex items-center justify-center" data-testid={`${testId}-empty`}>
        Add 2+ snapshots to see a trend.
      </div>
    );
  }

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${height - 6} L ${points[0].x} ${height - 6} Z`;
  const hover = hoverIdx != null ? points[hoverIdx] : null;
  const last = points[points.length - 1];

  return (
    <div className="relative" data-testid={testId}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        preserveAspectRatio="none"
        style={{ display: 'block' }}
      >
        <defs>
          <linearGradient id={`spark-grad-${accent.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={accent} stopOpacity="0.35" />
            <stop offset="100%" stopColor={accent} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill={`url(#spark-grad-${accent.replace('#', '')})`} />
        <path d={linePath} stroke={accent} strokeWidth="1.8" fill="none" strokeLinejoin="round" strokeLinecap="round" />
        {points.map((p, i) => (
          <g key={i} onMouseEnter={() => setHoverIdx(i)} onMouseLeave={() => setHoverIdx(null)}>
            <circle cx={p.x} cy={p.y} r="6" fill="transparent" style={{ cursor: 'pointer' }} />
            <circle cx={p.x} cy={p.y} r={hoverIdx === i ? 3.5 : 2.2} fill={accent} stroke="white" strokeWidth="1" />
          </g>
        ))}
        {/* Latest value label */}
        <text
          x={last.x - 4}
          y={Math.max(10, last.y - 6)}
          fontSize="9"
          fontWeight="600"
          textAnchor="end"
          fill={accent}
        >
          {formatValue(last.value)}
        </text>
      </svg>
      {hover && (
        <div
          className="absolute pointer-events-none bg-slate-900 text-white text-[10px] px-2 py-1 rounded shadow-lg whitespace-nowrap"
          style={{
            left: `${(hover.x / width) * 100}%`,
            top: `${(hover.y / height) * 100}%`,
            transform: 'translate(-50%, -120%)',
          }}
          data-testid={`${testId}-tooltip`}
        >
          <div className="opacity-70">{hover.label}</div>
          <div className="font-semibold">{formatValue(hover.value)}</div>
        </div>
      )}
      {showAxis && (
        <div className="flex justify-between mt-1 text-[10px] text-slate-400">
          <span>{points[0].label}</span>
          <span>{points[points.length - 1].label}</span>
        </div>
      )}
    </div>
  );
}
